import React from "react";
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import { Tooltip, IconButton, Input, Button } from "@mui/joy";
import InfoIcon from "@mui/icons-material/Info";
import { Edit as EditIcon, Save as SaveIcon, Error as ErrorIcon } from "@mui/icons-material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';
import RecursiveItems from "../../components/RecursiveItems";
import RecursiveEditableItems from "../../components/RecursiveEditableItems";
import RecursiveItemsComparison from "../../components/RecursiveItemsComparison";

const checkMatch = (val1, val2) => {
  if (val1 === val2) return true;
  if (typeof val1 === 'object' && val1 !== null && typeof val2 === 'object' && val2 !== null) {
    return JSON.stringify(val1) === JSON.stringify(val2);
  }
  return val1 === val2;
};

const ExtractedFieldsCard = ({
  getFields,
  getOutput,
  result,
  isEditing,
  onEditToggle,
  onSave,
  onFieldChange,
  setDocumentPage,
  groundTruth,
  isEvaluation,
  onFieldHover,
  fieldBboxes,
  onShowField,
}) => {
  if (!getFields || Object.keys(getFields).length === 0) {
    return null;
  }

  // Helper to render read-only values (extracted or ground truth)
  const renderReadOnlyValue = (value) => {
    if (value && typeof value === "object") {
      return (
        <Card variant="outlined" sx={{ p: 2, minWidth: 0, overflowX: "hidden" }}>
          <RecursiveItems item={value} handleClick={setDocumentPage} />
        </Card>
      );
    }

    if (typeof value === "boolean") {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          {value ? (
            <>
              <CheckCircleIcon color="success" />
              <Chip color="success" variant="soft" size="sm">
                True
              </Chip>
            </>
          ) : (
            <>
              <CancelIcon color="danger" />
              <Chip color="danger" variant="soft" size="sm">
                False
              </Chip>
            </>
          )}
        </Stack>
      );
    }

    return (
      <Typography
        level="body-sm"
        component="pre"
        sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", minWidth: 0 }}
      >
        {value !== null && value !== undefined && value !== ""
          ? value
          : "No Data Available"}
      </Typography>
    );
  };

  // Helper to render the extracted field (editable or read-only)
  const renderExtractedField = (key, value, errorMessage) => {
    if (isEditing) {
      if (value && typeof value === "object") {
        return (
          <Card variant="outlined" sx={{ p: 2, minWidth: 0, overflowX: "hidden" }}>
            <RecursiveEditableItems
              item={value}
              onChange={(updatedValue) =>
                onFieldChange({
                  target: { name: key, value: updatedValue },
                })
              }
            />
          </Card>
        );
      }
      return (
        <Input
          name={key}
          onChange={onFieldChange}
          value={value ?? ""}
          placeholder="Editing..."
          error={Boolean(errorMessage)}
          sx={{ mb: errorMessage ? 1 : 0 }}
        />
      );
    }

    return renderReadOnlyValue(value);
  };

  const hasFieldBbox = (fieldKey) => Boolean(fieldBboxes?.[fieldKey]?.bbox);

  const renderShowButton = (fieldKey, options = {}) => {
    const { stopPropagation = false, renderAsSpan = false } = options;
    if (!hasFieldBbox(fieldKey)) return null;

    const triggerShow = (event) => {
      if (stopPropagation) {
        event.preventDefault();
        event.stopPropagation();
      }
      onShowField?.(fieldKey);
    };

    if (renderAsSpan) {
      const handleKeyDown = (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        triggerShow(event);
      };

      return (
        <Button
          component="span"
          variant="soft"
          color="danger"
          size="sm"
          tabIndex={0}
          onClick={triggerShow}
          onKeyDown={handleKeyDown}
          aria-label={`Show ${fieldKey} on document`}
          sx={{ minHeight: 'unset', px: 1, py: 0.25 }}
        >
          Show
        </Button>
      );
    }

    return (
      <Button
        variant="soft"
        color="danger"
        size="sm"
        onClick={triggerShow}
        aria-label={`Show ${fieldKey} on document`}
        sx={{ minHeight: 'unset', px: 1, py: 0.25 }}
      >
        Show
      </Button>
    );
  };

  // Calculate stats for evaluation
  const totalFields = Object.keys(getFields).length;
  const correctFields = Object.entries(getFields).filter(([key, value]) => {
    return checkMatch(value, groundTruth?.[key]);
  }).length;

  return (
    <Card variant="outlined" sx={{ px: 3, py: 2, minWidth: 0, overflowX: "hidden" }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction={{ sm: "column", lg: "row" }} justifyContent="center" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack direction="row" alignItems="center">
                <Typography level="h3">Extracted Fields</Typography>
                <Tooltip
                  title="The confidence score defines how well was the information extracted based on the image"
                  arrow
                  placement="right"
                >
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              {isEvaluation && (
                <Chip
                  color={correctFields === totalFields ? "success" : "warning"}
                  variant="soft"
                  size="lg"
                >
                  {correctFields}/{totalFields} fields are correct
                </Chip>
              )}
            </Stack>
          </Stack>

          <IconButton onClick={isEditing ? onSave : onEditToggle} color="primary">
            {isEditing ? <SaveIcon /> : <EditIcon />}
          </IconButton>
        </Stack>

        {/* Fields list */}
        <Box sx={{ minWidth: 0 }}>
          {Object.entries(getFields).map(([key, value], index) => {
            const errorMessage =
              getOutput?.extractionStats?.error_fields?.[key] ?? null;
            const isLastItem = index === Object.keys(getFields).length - 1;
            const groundTruthValue = groundTruth?.[key];
            const isMatch = isEvaluation ? checkMatch(value, groundTruthValue) : null;

            const isSimpleField = !isEvaluation && (value === null || value === undefined || typeof value !== "object");

            if (isSimpleField) {
              return (
                <Box
                  key={key}
                  onMouseEnter={() => onFieldHover?.(key)}
                  onMouseLeave={() => onFieldHover?.(null)}
                  sx={{
                    py: 1.5,
                    borderBottom: isLastItem ? "none" : "1px solid",
                    borderColor: "divider",
                    cursor: "default",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Typography level="title-sm" sx={{ minWidth: 150, flexShrink: 0, pt: isEditing ? 0.75 : 0 }}>
                      {key}:
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0, pt: isEditing ? 0 : 0.1 }}>
                      {renderExtractedField(key, value, errorMessage)}
                    </Box>
                    {renderShowButton(key)}
                  </Stack>

                  {result?.output?.encoding &&
                    Object.keys(result.output.encoding).includes(key) && (
                      <Typography level="body-sm" sx={{ ml: 2, mt: 1 }}>
                        <strong>encoding:</strong>{" "}
                        {result.output.encoding[key] === "" ||
                          result.output.encoding[key] == null
                          ? "No encoding value"
                          : result.output.encoding[key]}
                      </Typography>
                    )}

                  {errorMessage && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        mt: 1,
                        pl: 1,
                        color: "error.main",
                        bgcolor: "error.lighter",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <ErrorIcon fontSize="small" color="error" />
                      <Typography level="body-sm" color="error.main">
                        {errorMessage}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              );
            }

            return (
              <Accordion
                key={key}
                spacing={1}
                defaultExpanded
                onMouseEnter={() => onFieldHover?.(key)}
                onMouseLeave={() => onFieldHover?.(null)}
                sx={{
                  mb: 1,
                  py: 2,
                  borderBottom: isLastItem ? "none" : "1px solid #ddd",
                }}
              >
                <AccordionSummary>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: "100%" }}
                  >
                    <Typography level="title-md">{key}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {isEvaluation && (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {isMatch ?
                            <CheckCircleIcon color="success" fontSize="large" /> :
                            <CancelIcon color="danger" fontSize="large" />
                          }
                        </Box>
                      )}
                      {renderShowButton(key, { stopPropagation: true, renderAsSpan: true })}
                    </Stack>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  {isEvaluation ? (
                    !isEditing && ((typeof value === "object" && value !== null) || (typeof groundTruthValue === "object" && groundTruthValue !== null)) ? (
                      <Card variant="outlined" sx={{ p: 2, minWidth: 0, overflowX: "hidden" }}>
                        <RecursiveItemsComparison
                          extracted={value}
                          groundTruth={groundTruthValue}
                        />
                      </Card>
                    ) : (
                      <Grid container spacing={2} sx={{ minWidth: 0 }}>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                          <Typography level="title-sm" mb={1} color="primary">Extracted Data</Typography>
                          {renderExtractedField(key, value, errorMessage)}
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                          <Typography level="title-sm" mb={1} color="success">Ground Truth</Typography>
                          {renderReadOnlyValue(groundTruthValue)}
                        </Grid>
                      </Grid>
                    )
                  ) : (
                    renderExtractedField(key, value, errorMessage)
                  )}

                  {result?.output?.encoding &&
                    Object.keys(result.output.encoding).includes(key) && (
                      <Typography level="body-sm" sx={{ ml: 2, mt: 1 }}>
                        <strong>encoding:</strong>{" "}
                        {result.output.encoding[key] === "" ||
                          result.output.encoding[key] == null
                          ? "No encoding value"
                          : result.output.encoding[key]}
                      </Typography>
                    )}

                  {errorMessage && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        mt: 1,
                        pl: 1,
                        color: "error.main",
                        bgcolor: "error.lighter",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <ErrorIcon fontSize="small" color="error" />
                      <Typography level="body-sm" color="error.main">
                        {errorMessage}
                      </Typography>
                    </Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

      </CardContent>
    </Card>
  );
};

export default ExtractedFieldsCard;
